import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { importSessionsApi, type ChatMessage, type ImportSession, type Readiness } from '../lib/api';
import { useBasePath, withBase } from '../lib/basePath';

// Чат импорта прайсов для продавца. История живёт на сервере (переживает F5).
// Ответы приходят синхронно в ответе POST — поллинга нет.
export default function ImportChat() {
  const base = useBasePath();
  const [session, setSession] = useState<ImportSession | null>(null);
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [msgs, setMsgs] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    importSessionsApi.open()
      .then((r) => { setSession(r.session); setMsgs(r.session.messages); setReadiness(r.readiness); })
      .catch((e) => setErr(String(e?.message || e)));
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs.length, busy]);

  function applyTurn(t: { messages: ChatMessage[]; readiness: Readiness }) {
    setMsgs((p) => [...p, ...t.messages]);
    setReadiness(t.readiness);
  }

  async function send(body: { text?: string; button_id?: string; question_id?: string }, echo?: string) {
    if (!session || busy) return;
    setBusy(true); setErr(null);
    const echoId = `local_${Date.now()}`;
    if (echo) setMsgs((p) => [...p, { id: echoId, role: 'seller', ts: new Date().toISOString(), text: echo }]);
    try { applyTurn(await importSessionsApi.message(session.id, body)); }
    catch (e: any) {
      // Сервер сообщение не записал — убираем локальное эхо, чтобы лента не врала.
      if (echo) setMsgs((p) => p.filter((m) => m.id !== echoId));
      setErr(e?.message === 'busy' ? 'Подождите, я ещё обрабатываю предыдущее сообщение.' : String(e?.message || e));
    }
    finally { setBusy(false); }
  }

  async function sendFiles(files: File[]) {
    if (!session || busy || !files.length) return;
    setBusy(true); setErr(null);
    const echoStamp = `localf_${Date.now()}`;
    setMsgs((p) => [...p, ...files.map((f, i) => ({ id: `${echoStamp}_${i}`, role: 'seller' as const, ts: new Date().toISOString(), text: `Файл: ${f.name}` }))]);
    try { applyTurn(await importSessionsApi.files(session.id, files)); }
    catch (e: any) {
      setMsgs((p) => p.filter((m) => !m.id.startsWith(echoStamp)));
      setErr(e?.message === 'upload_limit' ? 'Файл слишком большой (до 50 МБ) или файлов больше 10.' : String(e?.message || e));
    }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText('');
    void send({ text: t }, t);
  };

  if (err && !session) return <div className="container"><p className="error">{err}</p></div>;
  if (!session) return <div className="container"><p className="muted">Загрузка…</p></div>;

  return (
    <div className="import-chat">
      {readiness && readiness.total > 0 && (
        <div className="chat-readiness">
          <strong>{readiness.total}</strong>&nbsp;товаров: <span className="chat-ok">{readiness.ready} готовы</span>
          {readiness.draft > 0 && <> · <span className="chat-warn">{readiness.draft} черновиков</span></>}
          {readiness.no_photo > 0 && <> · без фото: {readiness.no_photo}</>}
          {readiness.draft > 0 && (
            <Link to={withBase(base, '/products?status=draft')} className="chat-readiness-link">К черновикам →</Link>
          )}
        </div>
      )}

      <div className="chat-feed">
        {msgs.map((m) => (
          <div key={m.id} className={`chat-msg chat-msg-${m.role}`}>
            <div className="chat-bubble">
              {m.text.split('\n').map((line, i) => <p key={i}>{line}</p>)}
              {(m.buttons || []).length > 0 && (
                <div className="chat-buttons">
                  {m.buttons!.map((b) => (
                    <button key={b.id} type="button" disabled={busy}
                      onClick={() => void send({ button_id: b.id, question_id: m.question_id }, b.label)}>
                      {b.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && <div className="chat-msg chat-msg-assistant"><div className="chat-bubble chat-typing">Обрабатываю…</div></div>}
        {err && <p className="error">{err}</p>}
        <div ref={bottomRef} />
      </div>

      <form className="chat-input" onSubmit={onSubmit}>
        <button type="button" className="chat-attach" title="Прикрепить файлы" disabled={busy}
          onClick={() => fileRef.current?.click()}>📎</button>
        <input ref={fileRef} type="file" multiple hidden
          accept=".xlsx,.csv,.zip,image/jpeg,image/png,image/webp"
          onChange={(e) => void sendFiles(Array.from(e.target.files || []))} />
        <input type="text" value={text} placeholder="Напишите ответ или вопрос…"
          onChange={(e) => setText(e.target.value)} disabled={busy} />
        <button type="submit" disabled={busy || !text.trim()}>Отправить</button>
      </form>
    </div>
  );
}
